import React, { ChangeEvent, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { Modal } from "@mui/material";
import { NumberInput } from "@mantine/core";
import {
  NewOrderImpressionsMode,
  NewOrderType,
  TNewOrderCountry,
  TNewOrderForm,
  TViewsKeyword,
} from "@/entities/new-order";
import {
  getDatesListFromCountry,
  getKeysFromCountry,
} from "@/entities/new-order/utils";
import TasksRatioModal from "@/Pages/CreateCampaignPage/Presentor/Modules/CreateCampaignCountriesList/Modals/TasksRatioModal";
import modalStyles from "@/Theme/modalsElements.module.scss";
import { calcImpressions, getTableTitle, useRatioHandler } from "../lib";
import { RemoveRowButton, RatioButton, ClearButton } from "./buttons";
import { CRNumberInput } from "./inputs";
import styles from "./country-item.module.scss";

type TCountryKeywordsTableProps = {
  countryIndex: number;
  autofill: boolean;
};

export const CountryKeywordsTable = ({
  countryIndex,
  autofill,
}: TCountryKeywordsTableProps) => {
  const { control, watch } = useFormContext<TNewOrderForm>();

  const { update } = useFieldArray({
    control: control,
    name: "countriesList",
  });

  const { type, countriesList } = watch();

  const country = countriesList[countryIndex];

  const { impressions } = country;

  // Локальное состояние для временного хранения значений Impressions во время редактирования
  const [tempImpressionValues, setTempImpressionValues] = useState<
    Record<string, string | number>
  >({});

  const {
    ratioKey,
    ratioModalOpen,
    openRatioModal,
    closeRatioModal,
    setRatio,
  } = useRatioHandler({
    country,
    updateCountry: (newCountry) => update(countryIndex, newCountry),
    countryIndex,
  });

  const handleChangeTableInput = (
    e: ChangeEvent<HTMLInputElement>,
    date: string,
    keyword: string,
    dateIndex: number,
  ) => {
    //обработчик ввода значений в поля в таблице
    const regex = /^[0-9\b]+$/;
    const value = e.target.value;

    if (regex.test(value) || value === "") {
      const newCountry = JSON.parse(JSON.stringify(country));

      const cellValue = newCountry.keywords[date][keyword];
      if (typeof cellValue === "number") {
        newCountry.keywords[date][keyword] = Number(value);
      } else if (typeof cellValue === "object" && cellValue !== null) {
        const CRValue = cellValue.CR;
        const isCRMode = impressions?.type === NewOrderImpressionsMode.CR;

        newCountry.keywords[date][keyword].installs = Number(value);
        // Пересчитываем views только в режиме CR и если CR задан
        if (isCRMode && CRValue) {
          newCountry.keywords[date][keyword].views = calcImpressions(
            Number(value),
            CRValue,
          );
        }
      } else {
        // Если структура данных некорректна, создаем правильную
        if (impressions) {
          newCountry.keywords[date][keyword] = {
            installs: Number(value),
            views: 0,
            CR: 100,
          };
        } else {
          newCountry.keywords[date][keyword] = Number(value);
        }
      }
      if (dateIndex === 0 && autofill) {
        Object.keys(country.keywords).map((date) => {
          if (typeof cellValue === "number") {
            newCountry.keywords[date][keyword] = Number(e.target.value);
          } else if (typeof cellValue === "object" && cellValue !== null) {
            const CRValue = newCountry.keywords[date][keyword]?.CR;
            const isCRMode = impressions?.type === NewOrderImpressionsMode.CR;
            newCountry.keywords[date][keyword].installs = Number(value);

            // Пересчитываем views только в режиме CR и если CR задан
            if (isCRMode && CRValue) {
              newCountry.keywords[date][keyword].views = calcImpressions(
                Number(value),
                CRValue,
              );
            }
          } else {
            // Если структура данных некорректна, создаем правильную
            if (impressions) {
              newCountry.keywords[date][keyword] = {
                installs: Number(value),
                views: 0,
                CR: 100,
              };
            } else {
              newCountry.keywords[date][keyword] = Number(value);
            }
          }
        });
      }

      update(countryIndex, newCountry);
    }
  };

  const handleChangeRowCRInput = (value: number, keyword: string) => {
    // Обновляем CR для всех дат этого keyword
    const newCountry: TNewOrderCountry = JSON.parse(JSON.stringify(country));
    const dates = getDatesListFromCountry(newCountry);
    const keywords: TViewsKeyword = newCountry.keywords as TViewsKeyword;

    dates.map((date) => {
      const keywordData = keywords[date][keyword];

      // Защита от некорректной структуры
      if (typeof keywordData === "object" && keywordData !== null) {
        const cellInstalls = keywordData.installs ?? 0;

        keywords[date][keyword].views = calcImpressions(cellInstalls, value);
        keywords[date][keyword].CR = value;
      } else {
        // Если структура некорректна, создаем правильную
        keywords[date][keyword] = {
          installs: 0,
          views: 0,
          CR: value,
        };
      }
    });

    update(countryIndex, newCountry);
  };

  const handleChangeRowImpressionInput = (
    value: string | number,
    keyword: string,
  ) => {
    // Просто сохраняем временное значение в локальный стейт
    setTempImpressionValues((prev) => ({
      ...prev,
      [keyword]: value,
    }));
  };

  const handleBlurRowImpressionInput = (keyword: string) => {
    // Получаем текущее значение (из временного стейта или из формы)
    const tempValue = tempImpressionValues[keyword];
    let currentValue =
      tempValue !== undefined
        ? Number(tempValue)
        : Number(getImpressionInputValue(keyword));

    // Валидация: только целые неотрицательные числа
    currentValue = Math.max(0, Math.floor(currentValue)) || 0;

    // Очищаем временное значение
    setTempImpressionValues((prev) => {
      const newValues = { ...prev };
      delete newValues[keyword];
      return newValues;
    });

    // Применяем значение в глобальный стейт
    const newCountry: TNewOrderCountry = JSON.parse(JSON.stringify(country));
    const dates = getDatesListFromCountry(newCountry);
    const keywords: TViewsKeyword = newCountry.keywords as TViewsKeyword;

    dates.map((date) => {
      const keywordData = keywords[date][keyword];

      // Защита от некорректной структуры
      if (typeof keywordData === "object" && keywordData !== null) {
        keywords[date][keyword].views = currentValue;
      } else {
        // Если структура некорректна, создаем правильную
        keywords[date][keyword] = {
          installs: 0,
          views: currentValue,
          CR: 100,
        };
      }
    });

    update(countryIndex, newCountry);
  };

  const getImpressionInputValue = (keyword: string) => {
    // Если есть временное значение (во время редактирования), возвращаем его
    if (tempImpressionValues[keyword] !== undefined) {
      return tempImpressionValues[keyword];
    }

    // Иначе возвращаем значение из формы
    const dates = getDatesListFromCountry(country);
    const countryKeywords: TViewsKeyword = country.keywords as TViewsKeyword;
    const keywordData = countryKeywords[dates[0]]?.[keyword];

    // Защита от некорректной структуры
    if (typeof keywordData === "object" && keywordData !== null) {
      return keywordData.views ?? 0;
    }
    return 0;
  };

  const getCRInputValue = (keyword: string) => {
    const dates = getDatesListFromCountry(country);
    const countryKeywords: TViewsKeyword = country.keywords as TViewsKeyword;
    const keywordData = countryKeywords[dates[0]]?.[keyword];

    // Защита от некорректной структуры
    if (typeof keywordData === "object" && keywordData !== null) {
      return keywordData.CR ?? 100;
    }
    return 100;
  };

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th className={styles.table__th} data-role="title">
            <div className={styles.table__action_buttons}>
              <ClearButton
                onClick={() => setRatio("clearAll", 0, null)}
                role="globalClear"
                title="Clear the number of installations in all cells"
              />
              <RatioButton
                onClick={() => openRatioModal(null)}
                role="globalRatio"
                title="Cell autofill settings for all keywords"
              />
            </div>
          </th>
          {impressions?.type && (
            <th
              className={styles.table__th}
              data-role="title"
              style={{ textAlign: "center" }}
            >
              {impressions.type}
            </th>
          )}

          <th className={styles.table__th} data-role="title">
            {getTableTitle(type!)}
          </th>
          {getDatesListFromCountry(country).map((date) => (
            <th className={styles.table__th} key={date}>
              {date}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {getKeysFromCountry(country).map((keyword, rowIndex) => {
          return (
            <tr key={keyword}>
              <td className={styles.table__td} data-role="title">
                <div className={styles.table__action_buttons}>
                  {(type === NewOrderType.install ||
                    type === NewOrderType.install_with_views) && (
                    <RemoveRowButton
                      onClick={() => setRatio("deleteRow", null, keyword)}
                      title="Delete keyword"
                    />
                  )}
                  <ClearButton
                    onClick={() => setRatio("clearRow", null, keyword)}
                    role="clearRow"
                    title="Clear row"
                  />
                  <RatioButton
                    onClick={() => openRatioModal(keyword)}
                    role="rowRatio"
                    title="Ratio. To use this setting, specify the number of installations on the first day of the campaign for this keyword."
                  />
                </div>
              </td>
              {impressions?.type && (
                <td className={styles.table__td} data-role="title">
                  {impressions.type === NewOrderImpressionsMode.CR && (
                    <CRNumberInput
                      hideControls
                      value={getCRInputValue(keyword)}
                      onChange={(value) =>
                        handleChangeRowCRInput(value, keyword)
                      }
                      size="md"
                      suffix="%"
                      onFocus={(event) => event.target.select()}
                    />
                  )}
                  {impressions.type === NewOrderImpressionsMode.Impressions && (
                    <NumberInput
                      hideControls
                      size="md"
                      value={getImpressionInputValue(keyword)}
                      min={0}
                      decimalScale={0}
                      clampBehavior="strict"
                      allowNegative={false}
                      onFocus={(event) => event.target.select()}
                      onChange={(e) =>
                        handleChangeRowImpressionInput(e, keyword)
                      }
                      onBlur={() => handleBlurRowImpressionInput(keyword)}
                    />
                  )}
                </td>
              )}
              <td className={styles.table__td} data-role="title">
                {keyword === "#install_by_package#" ? "by package" : keyword}
              </td>
              {getDatesListFromCountry(country).map((date, dateIndex) => {
                const cellValue = Object.values(country.keywords[date])[
                  rowIndex
                ];
                // Безопасное извлечение числового значения
                const displayValue =
                  typeof cellValue === "number"
                    ? cellValue
                    : (cellValue?.installs ?? 0);

                return (
                  <td className={styles.table__td} key={date}>
                    <input
                      className={styles.table__input}
                      min={0}
                      value={displayValue}
                      onFocus={(event) => event.target.select()}
                      onChange={(e) =>
                        handleChangeTableInput(e, date, keyword, dateIndex)
                      }
                    />
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
      <Modal
        open={ratioModalOpen}
        onClose={closeRatioModal}
        className={modalStyles.modal}
      >
        <TasksRatioModal
          handleSetRatio={setRatio}
          ratioKey={ratioKey}
          onClose={closeRatioModal}
        />
      </Modal>
    </table>
  );
};
